import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StoryCard from "@/components/StoryCard";
import { RefreshCw, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["all", "World", "Politics", "Tech", "Business", "Science", "Health"];

export default function FeedPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [bookmarkIds, setBookmarkIds] = useState(new Set());
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadStories = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/news/list", { params: { category, search, sort: "heat", limit: 60 } });
      setStories(r.data);
    } catch {
      toast.error("Failed to load stories");
    } finally {
      setLoading(false);
    }
  }, [category, search]);

  const loadBookmarks = useCallback(async () => {
    if (!user) { setBookmarkIds(new Set()); return; }
    try {
      const r = await api.get("/bookmarks/ids");
      setBookmarkIds(new Set(r.data.ids));
    } catch (err) {
      console.warn("Failed to load bookmark ids", err);
    }
  }, [user]);

  useEffect(() => { loadStories(); }, [loadStories]);
  useEffect(() => { loadBookmarks(); }, [loadBookmarks]);

  // Initial auto-fetch if archive is empty
  useEffect(() => {
    if (!loading && stories.length === 0 && !lastRefresh) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, stories.length]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/news/refresh");
      toast.success(`Pulled ${r.data.fetched} signals · ${r.data.new} new · ${r.data.total_archive} archived`);
      setLastRefresh(new Date());
      await loadStories();
    } catch {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const toggleBookmark = async (storyId) => {
    const isBookmarked = bookmarkIds.has(storyId);
    try {
      if (isBookmarked) {
        await api.delete(`/bookmarks/${storyId}`);
        const next = new Set(bookmarkIds); next.delete(storyId); setBookmarkIds(next);
        toast.success("Removed from bookmarks");
      } else {
        await api.post("/bookmarks", { story_id: storyId });
        const next = new Set(bookmarkIds); next.add(storyId); setBookmarkIds(next);
        toast.success("Saved to bookmarks");
      }
    } catch {
      toast.error("Bookmark action failed");
    }
  };

  const requireAuth = () => {
    toast.error("Sign in to save bookmarks");
    nav("/login");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Hero / Masthead */}
      <section className="border-b-2 border-stone-900 pb-8 mb-10 archive-grain">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-3xl">
            <div className="font-mono-head text-xs uppercase tracking-[0.3em] text-stone-500 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-[#E60000] rounded-full animate-pulse" />
              Live Trending Signals · {stories.length} stories indexed
            </div>
            <h1 className="font-serif-head text-5xl sm:text-6xl text-stone-900 leading-[0.95] mb-4">
              The hottest stories.<br />
              <span className="italic text-[#E60000]">The hidden truths.</span>
            </h1>
            <p className="text-base text-stone-700 leading-relaxed max-w-2xl">
              An investigative archive that pulls the day's most-discussed news from across the web — then
              probes each story for missing context, bias, counter-narratives, and the angles mainstream
              outlets won't touch.
            </p>
          </div>

          <button
            data-testid="refresh-news-btn"
            onClick={refresh}
            disabled={refreshing}
            className="bg-stone-900 text-stone-50 font-mono-head text-sm uppercase tracking-[0.2em] px-6 py-4 hover:bg-[#E60000] transition-colors flex items-center gap-2 disabled:opacity-60"
          >
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {refreshing ? "Pulling Signals…" : "Pull Latest Signals"}
          </button>
        </div>
      </section>

      {/* Controls */}
      <section className="mb-8">
        <div className="flex items-center gap-2 border-b-2 border-stone-900 mb-4 pb-1">
          <Search className="w-4 h-4 text-stone-700" />
          <input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search headlines, sources, topics…"
            className="flex-1 bg-transparent font-mono-head text-base py-3 px-0 focus:outline-none placeholder-stone-400"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`cat-${c}`}
              onClick={() => setCategory(c)}
              className={`font-mono-head text-xs uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${
                category === c
                  ? "bg-stone-900 text-stone-50 border-stone-900"
                  : "bg-transparent text-stone-700 border-stone-300 hover:border-stone-900"
              }`}
            >
              {c === "all" ? "All Signals" : c}
            </button>
          ))}
        </div>
      </section>

      {/* Feed */}
      <section data-testid="story-feed">
        {loading ? (
          <div className="py-20 text-center font-mono-head text-xs uppercase tracking-widest text-stone-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Indexing archive…
          </div>
        ) : stories.length === 0 ? (
          <div className="py-20 text-center">
            <p className="font-mono-head text-sm uppercase tracking-widest text-stone-500">
              No stories yet. Click "Pull Latest Signals" to start indexing.
            </p>
          </div>
        ) : (
          stories.map((s, i) => (
            <StoryCard
              key={s.id}
              story={s}
              index={i}
              isBookmarked={bookmarkIds.has(s.id)}
              onToggleBookmark={toggleBookmark}
              requireAuth={!user ? requireAuth : null}
            />
          ))
        )}
      </section>
    </div>
  );
}
